/**
 * Trang Thông báo (`/thong-bao`) — hợp đồng A0 hồ sơ – thông báo mục 5.
 *
 * Tab "Thông báo của tôi": danh sách lọc/phân trang PHÍA MÁY CHỦ (GET /notifications),
 * dòng chưa đọc in đậm; bấm dòng → đánh dấu đã đọc rồi điều hướng `duongDan`.
 * Người có `notification.send` (chỉ cấp tỉnh) thấy thêm tab "Gửi thông báo" (soạn và
 * gửi xuống xã, xác nhận trước khi gửi) và "Đã gửi" (lịch sử, số đã đọc / số nhận).
 *
 * Sau mỗi lần đánh dấu đọc, gọi `napLaiSoChuaDoc()` để huy hiệu chuông và menu khớp ngay.
 */
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { dienVao } from '../ngon-ngu/vi';
import { viThongBao } from '../ngon-ngu/vi-thong-bao';
import {
  thongBaoApi,
  LoiApi,
  CAC_LOAI_THONG_BAO,
  CAC_MUC_DO,
  type DongThongBao,
  type DongThongBaoDaGui,
  type MucDoThongBao,
  type ApiThongBao,
} from '../api/thong-bao-api';
import type { TrangDuLieu } from '../api/quan-tri-api';
import { thietBiApi, type NutDiaBan } from '../api/thiet-bi-api';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { napLaiSoChuaDoc } from '../trang-thai/thong-bao-chua-doc';
import { moThongBao, nhanLoaiThongBao, thoiDiemThongBao } from './thong-bao-tien-ich';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { ChonNhieuDiaBan } from '../thanh-phan/ChonNhieuDiaBan';
import { HopXacNhan } from '../thanh-phan/HopXacNhan';
import { TheTag, TruongLoc, type KieuTag } from '../thanh-phan/MauGiaoDien';
import './quan-tri.css';
import './thong-bao.css';

const t = viThongBao;

/** Lớp API dùng ở trang — ghi đè được trong kiểm thử. */
export type ApiTrangThongBao = Pick<
  ApiThongBao,
  'danhSach' | 'danhDauDaDoc' | 'danhDauTatCaDaDoc' | 'guiThongBao' | 'lichSuGui'
> & { cayXa: (signal?: AbortSignal) => Promise<NutDiaBan[]> };

const API_MAC_DINH: ApiTrangThongBao = {
  danhSach: thongBaoApi.danhSach,
  danhDauDaDoc: thongBaoApi.danhDauDaDoc,
  danhDauTatCaDaDoc: thongBaoApi.danhDauTatCaDaDoc,
  guiThongBao: thongBaoApi.guiThongBao,
  lichSuGui: thongBaoApi.lichSuGui,
  cayXa: (signal) => thietBiApi.cayDiaBan({ cap: 'xa' }, signal),
};

/* --------------------------------- Tiện ích --------------------------------- */

const KIEU_MUC_DO: Record<MucDoThongBao, KieuTag> = {
  thong_thuong: 'trung-tinh',
  quan_trong: 'vang',
  khan: 'do',
};

function TheMucDo({ mucDo }: { mucDo: MucDoThongBao }): ReactNode {
  return (
    <span data-muc-do={mucDo}>
      <TheTag kieu={KIEU_MUC_DO[mucDo] ?? 'trung-tinh'}>{t.mucDo[mucDo] ?? mucDo}</TheTag>
    </span>
  );
}

function moTaLoi(goc: string, err: unknown): string {
  return err instanceof LoiApi && err.message ? `${goc}: ${err.message}` : goc;
}

/* --------------------------- Tab: Thông báo của tôi -------------------------- */

function DanhSachCuaToi({ api }: { api: ApiTrangThongBao }): ReactNode {
  const dieuHuong = useNavigate();
  const [chiChuaDoc, datChiChuaDoc] = useState(false);
  const [loai, datLoai] = useState('');
  const [tuNgay, datTuNgay] = useState('');
  const [denNgay, datDenNgay] = useState('');
  const [trang, datTrang] = useState(1);
  const [soDong, datSoDong] = useState(SO_DONG_MAC_DINH);
  const [moc, datMoc] = useState(0);
  const [thongBao, datThongBao] = useState<string | null>(null);
  const [loi, datLoi] = useState<string | null>(null);

  const dangLoc = chiChuaDoc || loai !== '' || tuNgay !== '' || denNgay !== '';

  const kq = dungTaiDuLieu<TrangDuLieu<DongThongBao>>({
    tai: (signal) =>
      api.danhSach(
        {
          page: trang,
          pageSize: soDong,
          chuaDoc: chiChuaDoc || undefined,
          loai: loai || undefined,
          tuNgay: tuNgay || undefined,
          denNgay: denNgay || undefined,
        },
        signal,
      ),
    laRong: (d) => d.items.length === 0,
    dangLoc,
    phuThuoc: [moc, trang, soDong, chiChuaDoc, loai, tuNgay, denNgay],
  });

  const taiLai = (): void => datMoc((x) => x + 1);

  const doiLoc =
    <V,>(dat: (v: V) => void) =>
    (v: V): void => {
      dat(v);
      datTrang(1);
    };

  const xoaLoc = (): void => {
    datChiChuaDoc(false);
    datLoai('');
    datTuNgay('');
    datDenNgay('');
    datTrang(1);
  };

  const danhDauTatCa = async (): Promise<void> => {
    datThongBao(null);
    datLoi(null);
    try {
      const r = await api.danhDauTatCaDaDoc();
      datThongBao(dienVao(t.daDanhDauTatCa, { so: r?.soDaDanhDau ?? 0 }));
      void napLaiSoChuaDoc();
      taiLai();
    } catch (err) {
      datLoi(moTaLoi(t.loiDanhDau, err));
    }
  };

  const bam = (d: DongThongBao): void => {
    void moThongBao(d, api.danhDauDaDoc, dieuHuong).then(() => {
      // Không điều hướng đi đâu thì tải lại để dòng hết in đậm.
      if (!d.duongDan && !d.daDoc) taiLai();
    });
  };

  const dong = kq.duLieu?.items ?? [];
  const cot: CotBang<DongThongBao>[] = [
    {
      khoa: 'stt',
      nhan: t.cot.stt,
      canGiua: true,
      ve: (d) => (trang - 1) * soDong + dong.indexOf(d) + 1,
    },
    { khoa: 'loai', nhan: t.cot.loai, ve: (d) => nhanLoaiThongBao(d.loai) },
    { khoa: 'nguoiGui', nhan: t.cot.nguoiGui, ve: (d) => d.nguoiGui?.ten ?? t.heThong },
    {
      khoa: 'noiDung',
      nhan: t.cot.noiDung,
      ve: (d) => (
        <div className="thong-bao__noi-dung">
          <button type="button" className="nut nut--lien-ket thong-bao__tieu-de" onClick={() => bam(d)}>
            {d.tieuDe}
          </button>
          {d.mucDo && d.mucDo !== 'thong_thuong' ? <TheMucDo mucDo={d.mucDo} /> : null}
          {d.noiDung ? <p className="thong-bao__mo-ta">{d.noiDung}</p> : null}
        </div>
      ),
    },
    { khoa: 'thoiDiem', nhan: t.cot.thoiDiem, ve: (d) => thoiDiemThongBao(d.createdAt) },
    {
      khoa: 'trangThai',
      nhan: t.cot.trangThai,
      ve: (d) => (
        <span data-da-doc={d.daDoc ? 'co' : 'khong'}>
          <TheTag kieu={d.daDoc ? 'trung-tinh' : 'xanh'}>{d.daDoc ? t.daDoc : t.chuaDoc}</TheTag>
        </span>
      ),
    },
  ];

  return (
    <section data-vung="thong-bao-cua-toi">
      <div className="thanh-loc-mau" role="search" data-vung="thanh-loc">
        <TruongLoc nhan={t.loc.trangThai} htmlFor="tb-loc-trang-thai">
          <select
            id="tb-loc-trang-thai"
            value={chiChuaDoc ? 'chua-doc' : ''}
            onChange={(e) => doiLoc(datChiChuaDoc)(e.target.value === 'chua-doc')}
          >
            <option value="">{t.loc.tatCa}</option>
            <option value="chua-doc">{t.loc.chuaDoc}</option>
          </select>
        </TruongLoc>
        <TruongLoc nhan={t.loc.loai} htmlFor="tb-loc-loai">
          <select id="tb-loc-loai" value={loai} onChange={(e) => doiLoc(datLoai)(e.target.value)}>
            <option value="">{t.loc.tatCaLoai}</option>
            {CAC_LOAI_THONG_BAO.map((l) => (
              <option key={l} value={l}>
                {nhanLoaiThongBao(l)}
              </option>
            ))}
          </select>
        </TruongLoc>
        <TruongLoc nhan={t.loc.khoangNgay} htmlFor="tb-tu-ngay">
          <span className="thong-bao__khoang-ngay">
            <input
              id="tb-tu-ngay"
              type="date"
              aria-label={`${t.loc.khoangNgay} — ${t.loc.tuNgay}`}
              value={tuNgay}
              max={denNgay || undefined}
              onChange={(e) => doiLoc(datTuNgay)(e.target.value)}
            />
            <span aria-hidden="true">→</span>
            <input
              id="tb-den-ngay"
              type="date"
              aria-label={`${t.loc.khoangNgay} — ${t.loc.denNgay}`}
              value={denNgay}
              min={tuNgay || undefined}
              onChange={(e) => doiLoc(datDenNgay)(e.target.value)}
            />
          </span>
        </TruongLoc>
        <div className="thong-bao__nut-loc">
          <button
            type="button"
            className="nut"
            data-vung="danh-dau-tat-ca"
            onClick={() => void danhDauTatCa()}
          >
            {t.danhDauTatCa}
          </button>
        </div>
      </div>

      {thongBao ? <p role="status">{thongBao}</p> : null}
      {loi ? (
        <p className="dai-thong-bao dai-thong-bao--loi" role="alert">
          {loi}
        </p>
      ) : null}

      <BangDuLieu<DongThongBao>
        nhan={t.tieuDe}
        cot={cot}
        dong={dong}
        khoaDong={(d) => d.id}
        lopDong={(d) => (d.daDoc ? undefined : 'thong-bao__dong--chua-doc')}
        onBamDong={bam}
        trangThai={kq.trangThai}
        loi={kq.loi}
        taiLau={kq.taiLau}
        onThuLai={taiLai}
        tong={kq.duLieu?.total ?? 0}
        trang={trang}
        soDongMoiTrang={soDong}
        onDoiTrang={datTrang}
        onDoiSoDong={(s) => {
          datSoDong(s);
          datTrang(1);
        }}
        dangLoc={dangLoc}
        onXoaLoc={xoaLoc}
        moTaRong={t.rongMoTa}
      />
    </section>
  );
}

/* ----------------------------- Tab: Gửi thông báo ---------------------------- */

const vietHoaDau = (s: string): string => (s ? s.charAt(0).toLocaleUpperCase('vi') + s.slice(1) : s);

export function BieuMauGuiThongBao({
  api,
  onDaGui,
}: {
  api: ApiTrangThongBao;
  onDaGui?: (kq: { id: string; soNguoiNhan: number }) => void;
}): ReactNode {
  const phien = dungPhien();
  const [tieuDe, datTieuDe] = useState('');
  const [noiDung, datNoiDung] = useState('');
  const [mucDo, datMucDo] = useState<MucDoThongBao>('thong_thuong');
  const [toanTinh, datToanTinh] = useState(false);
  const [xaChon, datXaChon] = useState<string[]>([]);
  const [canXacNhan, datCanXacNhan] = useState(false);
  const [dangGui, datDangGui] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);
  const [thongBao, datThongBao] = useState<string | null>(null);

  const xa = dungTaiDuLieu<NutDiaBan[]>({
    tai: (signal) => api.cayXa(signal),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });
  const dsXa = useMemo(() => (xa.duLieu ?? []).filter((n) => n.cap === 'xa'), [xa.duLieu]);
  // Mã tỉnh: theo hồ sơ; thiếu (API cũ) thì lấy mã cha của xã.
  const maTinh = phien.hoSo?.maTinh ?? dsXa.find((x) => x.maCha)?.maCha ?? null;

  const kiemTra = (e: FormEvent): void => {
    e.preventDefault();
    datLoi(null);
    datThongBao(null);
    if (!tieuDe.trim()) return datLoi(t.gui.thieuTieuDe);
    if (!noiDung.trim()) return datLoi(t.gui.thieuNoiDung);
    if (toanTinh ? !maTinh : xaChon.length === 0) return datLoi(t.gui.thieuDiaBan);
    datCanXacNhan(true);
  };

  const gui = async (): Promise<void> => {
    datCanXacNhan(false);
    datDangGui(true);
    try {
      const kq = await api.guiThongBao({
        tieuDe: tieuDe.trim(),
        noiDung: noiDung.trim(),
        mucDo,
        diaBan: toanTinh && maTinh ? [maTinh] : xaChon,
      });
      datThongBao(dienVao(t.gui.daGui, { so: kq.soNguoiNhan }));
      datTieuDe('');
      datNoiDung('');
      datMucDo('thong_thuong');
      datToanTinh(false);
      datXaChon([]);
      onDaGui?.(kq);
    } catch (err) {
      datLoi(moTaLoi(t.gui.loiGui, err));
    } finally {
      datDangGui(false);
    }
  };

  return (
    <form
      className="bieu-mau-hep thong-bao__bieu-mau"
      data-vung="gui-thong-bao"
      onSubmit={kiemTra}
      noValidate
    >
      <div className="truong">
        <label htmlFor="tbg-tieu-de">
          {t.gui.tieuDe}
          <span className="truong__bat-buoc">*</span>
        </label>
        <input
          id="tbg-tieu-de"
          value={tieuDe}
          maxLength={200}
          aria-describedby="tbg-tieu-de-goi-y"
          onChange={(e) => datTieuDe(e.target.value)}
        />
        <small id="tbg-tieu-de-goi-y" className="truong__mo-ta">
          {t.gui.gioiHanTieuDe}
        </small>
      </div>
      <div className="truong">
        <label htmlFor="tbg-noi-dung">
          {t.gui.noiDung}
          <span className="truong__bat-buoc">*</span>
        </label>
        <textarea
          id="tbg-noi-dung"
          rows={6}
          value={noiDung}
          maxLength={4000}
          aria-describedby="tbg-noi-dung-goi-y"
          onChange={(e) => datNoiDung(e.target.value)}
        />
        <small id="tbg-noi-dung-goi-y" className="truong__mo-ta">
          {t.gui.gioiHanNoiDung}
        </small>
      </div>
      <div className="truong">
        <label htmlFor="tbg-muc-do">{t.gui.mucDo}</label>
        <select id="tbg-muc-do" value={mucDo} onChange={(e) => datMucDo(e.target.value as MucDoThongBao)}>
          {CAC_MUC_DO.map((m) => (
            <option key={m} value={m}>
              {t.mucDo[m]}
            </option>
          ))}
        </select>
      </div>
      <fieldset className="truong" data-vung="tbg-dia-ban">
        <legend>
          {t.gui.diaBan}
          <span className="truong__bat-buoc">*</span>
        </legend>
        <label>
          <input type="radio" name="tbg-pham-vi" checked={toanTinh} onChange={() => datToanTinh(true)} />{' '}
          {t.gui.toanTinh}
        </label>
        <label>
          <input type="radio" name="tbg-pham-vi" checked={!toanTinh} onChange={() => datToanTinh(false)} />{' '}
          {t.gui.chonXa}
        </label>
        <ChonNhieuDiaBan
          id="tbg-xa"
          nhan={t.gui.diaBan}
          ds={dsXa.map((x) => ({ ma: x.maDinhDanh, ten: vietHoaDau(x.ten) }))}
          giaTri={toanTinh ? [] : xaChon}
          onDoi={datXaChon}
          khoa={toanTinh}
          goiY={toanTinh ? t.gui.toanTinh : t.gui.goiYChonXa}
          nhanDaChon={(so) => dienVao(t.gui.daChonXa, { so })}
          nhanTatCa={t.gui.chonTatCa}
          nhanBoChon={t.gui.boChonTatCa}
          goiYTim={t.gui.timXa}
        />
      </fieldset>

      {thongBao ? <p role="status">{thongBao}</p> : null}
      {loi ? (
        <p className="dai-thong-bao dai-thong-bao--loi" role="alert">
          {loi}
        </p>
      ) : null}

      <button type="submit" className="nut nut--chinh" disabled={dangGui}>
        {dangGui ? t.gui.dangGui : t.gui.nutGui}
      </button>

      {canXacNhan ? (
        <HopXacNhan
          tieuDe={t.gui.xacNhanTieuDe}
          heQua={
            toanTinh
              ? dienVao(t.gui.xacNhanToanTinh, { tieuDe: tieuDe.trim() })
              : dienVao(t.gui.xacNhanXa, { tieuDe: tieuDe.trim(), so: xaChon.length })
          }
          ghiChu={t.gui.ghiChuXacNhan}
          nhanThucHien={t.gui.nutGui}
          onHuy={() => datCanXacNhan(false)}
          onThucHien={() => void gui()}
        />
      ) : null}
    </form>
  );
}

/* --------------------------------- Tab: Đã gửi ------------------------------- */

function LichSuGui({ api, moc }: { api: ApiTrangThongBao; moc: number }): ReactNode {
  const [trang, datTrang] = useState(1);
  const [soDong, datSoDong] = useState(SO_DONG_MAC_DINH);
  const [mocTai, datMocTai] = useState(0);
  const kq = dungTaiDuLieu<TrangDuLieu<DongThongBaoDaGui>>({
    tai: (signal) => api.lichSuGui({ page: trang, pageSize: soDong }, signal),
    laRong: (d) => d.items.length === 0,
    dangLoc: false,
    phuThuoc: [moc, mocTai, trang, soDong],
  });
  const cot: CotBang<DongThongBaoDaGui>[] = [
    { khoa: 'tieuDe', nhan: t.daGui.cot.tieuDe, ve: (d) => d.tieuDe },
    { khoa: 'mucDo', nhan: t.daGui.cot.mucDo, ve: (d) => <TheMucDo mucDo={d.mucDo} /> },
    {
      khoa: 'diaBan',
      nhan: t.daGui.cot.diaBan,
      ve: (d) => (d.tenDiaBan?.length ? d.tenDiaBan : d.diaBan).map(vietHoaDau).join(', '),
    },
    {
      khoa: 'daDoc',
      nhan: t.daGui.cot.daDoc,
      canGiua: true,
      ve: (d) => <span data-vung="ty-le-da-doc">{`${d.soDaDoc} / ${d.soNguoiNhan}`}</span>,
    },
    { khoa: 'nguoiGui', nhan: t.daGui.cot.nguoiGui, ve: (d) => d.nguoiGui?.ten ?? t.heThong },
    { khoa: 'thoiDiem', nhan: t.daGui.cot.thoiDiem, ve: (d) => thoiDiemThongBao(d.createdAt) },
  ];
  return (
    <section data-vung="thong-bao-da-gui">
      <BangDuLieu<DongThongBaoDaGui>
        nhan={t.tab.daGui}
        cot={cot}
        dong={kq.duLieu?.items ?? []}
        khoaDong={(d) => d.id}
        trangThai={kq.trangThai}
        loi={kq.loi}
        taiLau={kq.taiLau}
        onThuLai={() => datMocTai((x) => x + 1)}
        tong={kq.duLieu?.total ?? 0}
        trang={trang}
        soDongMoiTrang={soDong}
        onDoiTrang={datTrang}
        onDoiSoDong={(s) => {
          datSoDong(s);
          datTrang(1);
        }}
        dangLoc={false}
        onXoaLoc={() => undefined}
        moTaRong={t.daGui.rongMoTa}
      />
    </section>
  );
}

/* --------------------------------- Màn hình -------------------------------- */

type TabThongBao = 'cua-toi' | 'gui' | 'da-gui';

export interface TrangThongBaoProps {
  api?: ApiTrangThongBao;
}

export function TrangThongBao({ api = API_MAC_DINH }: TrangThongBaoProps = {}): ReactNode {
  const phien = dungPhien();
  // Gửi thông báo chỉ dành cho cấp tỉnh có quyền `notification.send`.
  const duocGui = phien.coQuyen('notification.send') && !phien.khoaDiaBan;
  const [tab, datTab] = useState<TabThongBao>('cua-toi');
  const [mocLichSu, datMocLichSu] = useState(0);

  useEffect(() => {
    if (!duocGui && tab !== 'cua-toi') datTab('cua-toi');
  }, [duocGui, tab]);

  const cacTab: [TabThongBao, string][] = duocGui
    ? [
        ['cua-toi', t.tab.cuaToi],
        ['gui', t.tab.gui],
        ['da-gui', t.tab.daGui],
      ]
    : [];

  return (
    <div className="man-hinh man-hinh--thong-bao" data-man-hinh="thong-bao">
      <header className="man-hinh__dau">
        <h1>{t.tieuDe}</h1>
        {duocGui && tab === 'cua-toi' ? (
          <div className="man-hinh__hanh-dong">
            <button type="button" className="nut nut--chinh" onClick={() => datTab('gui')}>
              {t.gui.nutGui}
            </button>
          </div>
        ) : null}
      </header>

      {cacTab.length > 0 ? (
        <div className="tab" role="tablist">
          {cacTab.map(([k, nhan]) => (
            <button
              key={k}
              type="button"
              role="tab"
              id={`tab-thong-bao-${k}`}
              aria-selected={tab === k}
              aria-controls="tab-thong-bao-noi-dung"
              className={`tab__nut${tab === k ? ' tab__nut--dang-chon' : ''}`}
              onClick={() => datTab(k)}
            >
              {nhan}
            </button>
          ))}
        </div>
      ) : null}

      <div
        id="tab-thong-bao-noi-dung"
        role={cacTab.length > 0 ? 'tabpanel' : undefined}
        aria-labelledby={cacTab.length > 0 ? `tab-thong-bao-${tab}` : undefined}
      >
        {tab === 'cua-toi' ? <DanhSachCuaToi api={api} /> : null}
        {tab === 'gui' && duocGui ? (
          <BieuMauGuiThongBao api={api} onDaGui={() => datMocLichSu((x) => x + 1)} />
        ) : null}
        {tab === 'da-gui' && duocGui ? <LichSuGui api={api} moc={mocLichSu} /> : null}
      </div>
    </div>
  );
}
